import { type RateRow, RatesPanel } from "@/components/rates/rates-panel";
import { AppLink } from "@/components/ui/app-link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { LoadingLabel } from "@/components/ui/loading-label";
import { LocalDate } from "@/components/ui/local-date";
import { Select } from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { DiscordLinkField } from "@/components/users/discord-link-field";
import { SecondaryEmails } from "@/components/users/secondary-emails";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { formatHours } from "@/lib/utils";
import type { GlobalRole } from "@prisma/client";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useState } from "react";

/**
 * `/users/:id` — admin-only user detail + edit. Server gates the
 * endpoint (403 -> redirect to /my). The edit form patches
 * globalRole + IBAN via `PATCH /api/users/:id`, toasts, and
 * invalidates the route.
 *
 * `GLOBAL_ROLE_LABEL` / `ALL_GLOBAL_ROLES` are inlined here rather
 * than porting the whole server-coupled `permissions.ts` — they're
 * two tiny constants.
 */

const GLOBAL_ROLE_LABEL: Record<GlobalRole, string> = {
  admin: "Admin",
  accountant: "Accountant",
};
const ALL_GLOBAL_ROLES: GlobalRole[] = ["admin", "accountant"];

const globalRoleVariant: Record<GlobalRole, "default" | "secondary" | "outline" | "destructive"> = {
  admin: "destructive",
  accountant: "default",
};

const approvalVariant: Record<
  string,
  "default" | "secondary" | "success" | "warning" | "destructive" | "outline"
> = {
  worked: "outline",
  approved: "success",
  paid: "default",
  rejected: "destructive",
};

const billingVariant: Record<string, "default" | "secondary" | "success" | "warning" | "outline"> =
  {
    unbilled: "outline",
    to_invoice: "warning",
    invoiced: "success",
    non_billable: "secondary",
  };

type UserDetail = {
  id: string;
  name: string | null;
  email: string;
  globalRole: GlobalRole | null;
  gitlabUserId: string | null;
  discordUsername: string | null;
  /** Set only when the bot resolved the handle to a real Discord account —
   *  that link, not the handle, is what a meeting is matched against. */
  discordUserId: string | null;
  iban: string | null;
  createdAt: string;
  timeEntries: Array<{
    id: string;
    hours: string;
    spentOn: string;
    approvalState: string;
    billingState: string;
    /** Null for untriaged / external Clockify imports — the row has
     *  no Hub project bound. `externalProjectName` keeps the
     *  Clockify-side name for display in that case. */
    project: { name: string } | null;
    externalProjectName: string | null;
    workItem: { title: string } | null;
  }>;
};

type UserDetailLoaderData = UserDetail & {
  /** Cost rates for this user. The `/users/:id` route is already
   *  admin-only, so `/api/rates` always answers — no role gate
   *  needed at this layer. */
  costRates: RateRow[];
};

export const Route = createFileRoute("/_app/users/$id")({
  loader: async ({ params }): Promise<UserDetailLoaderData> => {
    try {
      const [user, rates] = await Promise.all([
        api.get<UserDetail>(`/api/users/${params.id}`),
        api.get<{ rates: RateRow[] }>(
          `/api/rates?type=cost&userId=${encodeURIComponent(params.id)}&pageSize=200`,
        ),
      ]);
      return { ...user, costRates: rates.rates };
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        throw redirect({ to: "/my" });
      }
      throw err;
    }
  },
  component: UserDetailPage,
});

function UserDetailPage() {
  const user = Route.useLoaderData();

  // Client-sorted via the columns' `sortAccessor`s — no sort prop.
  const entryColumns: Array<DataTableColumn<UserDetail["timeEntries"][number]>> = [
    {
      key: "project",
      header: "Project",
      sortAccessor: (te) => te.project?.name ?? te.externalProjectName ?? "",
      cellClassName: "font-medium",
      cell: (te) =>
        te.project ? (
          te.project.name
        ) : te.externalProjectName ? (
          <span className="italic text-muted-foreground">Clockify: {te.externalProjectName}</span>
        ) : (
          <span className="italic text-muted-foreground">No project</span>
        ),
    },
    {
      key: "workItem",
      header: "Work Item",
      sortAccessor: (te) => te.workItem?.title,
      cellClassName: "text-sm text-muted-foreground max-w-[200px] truncate",
      cell: (te) => (te.workItem ? te.workItem.title : <span className="italic">overhead</span>),
    },
    {
      key: "hours",
      header: "Hours",
      sortAccessor: (te) => Number(te.hours),
      cell: (te) => formatHours(Number(te.hours)),
    },
    {
      key: "spentOn",
      header: "Date",
      sortAccessor: (te) => te.spentOn,
      cellClassName: "text-sm text-muted-foreground",
      cell: (te) => <LocalDate value={te.spentOn} />,
    },
    {
      key: "approval",
      header: "Approval",
      sortAccessor: (te) => te.approvalState,
      cell: (te) => (
        <Badge variant={approvalVariant[te.approvalState] ?? "outline"}>{te.approvalState}</Badge>
      ),
    },
    {
      key: "billing",
      header: "Billing",
      sortAccessor: (te) => te.billingState,
      cell: (te) => (
        <Badge variant={billingVariant[te.billingState] ?? "outline"}>{te.billingState}</Badge>
      ),
    },
  ];

  const maskedIban = user.iban
    ? `${user.iban.slice(0, 4)}${"*".repeat(Math.max(0, user.iban.length - 8))}${user.iban.slice(-4)}`
    : "---";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{user.name}</h1>
          <p className="text-muted-foreground mt-1">{user.email}</p>
        </div>
        <Button variant="outline" asChild>
          <AppLink href="/users">Back to Users</AppLink>
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Global Role</p>
            <p className="mt-1">
              {user.globalRole ? (
                <Badge variant={globalRoleVariant[user.globalRole]}>
                  {GLOBAL_ROLE_LABEL[user.globalRole]}
                </Badge>
              ) : (
                <span className="text-sm text-muted-foreground">—</span>
              )}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">GitLab ID</p>
            <p className="font-mono text-lg font-semibold">{user.gitlabUserId ?? "---"}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Discord</p>
            <p className="font-mono text-sm font-semibold" data-testid="discord-username">
              {user.discordUsername ?? "---"}
            </p>
            {/* A handle on its own does nothing — only a resolved account id
                lets a meeting propose time for this person. Stated in full on
                the Meeting identity card below; here it's just the glance. */}
            {user.discordUsername && (
              <p className="mt-1 text-xs">
                {user.discordUserId ? (
                  <span className="text-green-600">linked</span>
                ) : (
                  <span className="text-destructive">not linked</span>
                )}
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">IBAN</p>
            <p className="font-mono text-sm font-semibold">{maskedIban}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Edit User</CardTitle>
          <CardDescription>
            Update global role and payment information. Created <LocalDate value={user.createdAt} />
            .
          </CardDescription>
        </CardHeader>
        <CardContent>
          <EditUserForm userId={user.id} initialRole={user.globalRole} initialIban={user.iban} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Meeting identity</CardTitle>
          <CardDescription>
            Whether a recorded meeting can tell this participant is this person — and so whether it
            proposes their time at all. Discord matches on the account id the bot resolves from the
            handle; Google Meet matches on email and nothing else, so a Meet account on a different
            address than the Hub sign-in has to be claimed here. The user can maintain both
            themselves from their own profile.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <DiscordLinkField
            userId={user.id}
            initialHandle={user.discordUsername}
            initialLinked={Boolean(user.discordUserId)}
          />
          <div className="border-t pt-4">
            <SecondaryEmails userId={user.id} primaryEmail={user.email} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Time Entries</CardTitle>
          <CardDescription>{user.timeEntries.length} entries (showing latest 50)</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={entryColumns}
            rows={user.timeEntries}
            rowKey={(te) => te.id}
            emptyState="No time entries yet."
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Cost rates</CardTitle>
          <CardDescription>
            What this user's hour costs the company. Effective-dated — a raise is a new row with a
            later "Valid from", not an edit. Used for margin reports; not user-visible.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RatesPanel
            title=""
            type="cost"
            rates={user.costRates}
            anchor={{ kind: "user", userId: user.id, userName: user.name ?? user.email }}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function EditUserForm({
  userId,
  initialRole,
  initialIban,
}: {
  userId: string;
  initialRole: GlobalRole | null;
  initialIban: string | null;
}) {
  const router = useRouter();
  const [role, setRole] = useState<string>(initialRole ?? "");
  const [iban, setIban] = useState(initialIban ?? "");
  const update = useApiMutation(
    (body: { globalRole: string | null; iban: string | null }) =>
      api.patch(`/api/users/${userId}`, body),
    {
      onSuccess: () => {
        toast({ title: "User updated", variant: "success" });
        router.invalidate();
      },
      errorMessage: (err) => (err instanceof Error ? err.message : "Error"),
      onError: (msg) => toast({ title: "Update failed", description: msg, variant: "destructive" }),
    },
  );

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (update.pending) return;
    await update.mutate({
      globalRole: role === "" ? null : role,
      iban: iban.trim() || null,
    });
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="space-y-2">
        <label htmlFor="globalRole" className="text-sm font-medium leading-none">
          Global Role
        </label>
        <Select
          className="w-full"
          aria-label="Global Role"
          value={role}
          onChange={setRole}
          options={[
            { value: "", label: "None (project member only)" },
            ...ALL_GLOBAL_ROLES.map((r) => ({ value: r, label: GLOBAL_ROLE_LABEL[r] })),
          ]}
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="iban" className="text-sm font-medium leading-none">
          IBAN
        </label>
        <input
          id="iban"
          type="text"
          value={iban}
          onChange={(e) => setIban(e.target.value)}
          placeholder="e.g. CZ6508000000192000145399"
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
      </div>

      <Button type="submit" disabled={update.pending}>
        {update.pending ? <LoadingLabel text="Saving" /> : "Save Changes"}
      </Button>
    </form>
  );
}
